import uk from 'date-fns/locale/uk';

import { Refresh, ExpandLess, ExpandMore } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { useScrollSync } from '../../hooks/useScrollSync';

import { ConfirmDialog } from '../../components';
import { FiltersSection, TableHeader, ResultsTable } from './components';

import './SchedulerPage.css';
import { SchedulerFilters, SchedulerUpdateRequest } from '@/types/scheduler.types';
import {
  defaultFilters,
  getNextExpandedEntries,
  removeKeyFromMap,
  setMapEntryField,
  applyEditedValueToEntries,
  editKey,
  addOrReplacePendingUpdate
} from './shedular.utils';
import {
  useGetSchedulerEntriesQuery,
  useBulkUpdateSchedulerItemsMutation,
  useTriggerProcessingMutation
} from '@/services/shedular.api';
import { useSnackbar } from '@/hooks';

/**
 * Main page component for scheduler functionality
 * Manages scheduling of tasks with entry and substage management
 *
 * Features:
 * - Comprehensive filtering by search, date, time, status, user, and edit date
 * - Expandable entry rows with nested substages
 * - Inline editing for entry and substage data
 * - Save and process confirmation dialogs
 * - Real-time change tracking and validation
 * - Responsive design for mobile and desktop
 * - Ukrainian localization for date/time pickers
 */
function SchedulerPage() {
  const theme = useTheme();
  const { setSnackbarMessage } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { headerRef: tableHeaderRef, bodyRef: tableBodyRef } = useScrollSync();
  const [isExpanded, setIsExpanded] = useState(true);

  const [filters, setFilters] = useState<SchedulerFilters>(defaultFilters);
  const [expandedEntries, setExpandedEntries] = useState<number[]>([]);
  // Local scheduler state
  const [entries, setEntries] = useState<any[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<SchedulerUpdateRequest[]>([]);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmTriggerOpen, setConfirmTriggerOpen] = useState(false);

  const [editingItems, setEditingItems] = useState<Record<string, any>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});

  // RTK Query hooks
  const { data, isLoading } = useGetSchedulerEntriesQuery(filters, { refetchOnMountOrArgChange: true });
  const [bulkUpdateSchedulerItems, { isLoading: bulkUpdating }] = useBulkUpdateSchedulerItemsMutation();
  const [triggerProcessingTrigger] = useTriggerProcessingMutation();

  const hasChanges = pendingUpdates.length > 0;

  const getEditingValue = (type: 'entry' | 'substage', id: number) => editingItems[`${type}-${id}`];
  const isEditing = (type: 'entry' | 'substage', id: number) => `${type}-${id}` in editingItems;
  const hasItemChanges = (type: 'entry' | 'substage', id: number) => {
    const key = `${type}-${id}`;
    const original = originalValues[key];
    const current = editingItems[key];
    if (!original || !current) return false;
    return JSON.stringify(original) !== JSON.stringify(current);
  };

  useEffect(() => {
    if (data) {
      setEntries(data);
    }
  }, [data]);

  const handleFilterChange = (key: keyof SchedulerFilters, value: any) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
  };

  const handleToggleExpanded = (entryId: number) => {
    const nextEntries = getNextExpandedEntries(expandedEntries, entryId);
    setExpandedEntries(nextEntries);
  };

  const startEditing = (type: 'entry' | 'substage', id: number, currentValue: any) => {
    const key = editKey(type, id);
    setEditingItems(prev => ({ ...prev, [key]: currentValue }));
    setOriginalValues(prev => ({ ...prev, [key]: { ...currentValue } }));
  };

  const cancelEditing = (type: 'entry' | 'substage', id: number) => {
    const key = editKey(type, id);

    const nextEditingItems = removeKeyFromMap(editingItems, key);
    setEditingItems(nextEditingItems);

    const nextOriginalValues = removeKeyFromMap(originalValues, key);
    setOriginalValues(nextOriginalValues);
  };

  const updateEditingValue = (type: 'entry' | 'substage', id: number, field: string, value: any) => {
    const key = editKey(type, id);
    const nextEditingItems = setMapEntryField(editingItems, key, field, value);
    setEditingItems(nextEditingItems);
  };

  const saveEdit = (type: 'entry' | 'substage', id: number) => {
    const key = editKey(type, id);
    const editedValue = editingItems[key];
    if (!editedValue) return;

    const update: SchedulerUpdateRequest = { id, type, ...editedValue };

    setEntries(prev => applyEditedValueToEntries(prev, type, id, editedValue));

    setPendingUpdates(prev => {
      return addOrReplacePendingUpdate(prev, update);
    });

    setEditingItems(prev => removeKeyFromMap(prev, key));
    setOriginalValues(prev => removeKeyFromMap(prev, key));
  };

  const handleResetAll = () => {
    setPendingUpdates([]);
    setEditingItems({});
    setOriginalValues({});
    if (data) setEntries(data);
  };

  const handleSaveAll = () => {
    if (pendingUpdates.length === 0) return;
    setConfirmSaveOpen(true);
  };

  const confirmSave = async () => {
    setConfirmSaveOpen(false);
    try {
      const result = await bulkUpdateSchedulerItems({ updates: pendingUpdates }).unwrap();
      setPendingUpdates([]);
      setEditingItems({});
      setOriginalValues({});
      setSnackbarMessage(`Успішно збережено ${result.processed_count || pendingUpdates.length} змін`, 'success');
    } catch (error: any) {
      setSnackbarMessage('Помилка збереження: ' + (error?.data?.message || error.message || error), 'error');
    }
  };

  const confirmTriggerProcessing = async () => {
    setConfirmTriggerOpen(false);
    try {
      const result = await triggerProcessingTrigger(undefined).unwrap();
      setSnackbarMessage(`Обробка завершена: ${result.processed} оброблено, ${result.failed} помилок`, 'success');
    } catch (error: any) {
      setSnackbarMessage('Помилка запуску обробки: ' + (error?.data?.message || error.message || error), 'error');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
      <div className={`scheduler-container ${isMobile ? 'mobile' : ''}`}>
        {isLoading ? (
          <div className="scheduler-loading-container">
            <Refresh className="scheduler-loading-spinner" />
            <p>Завантаження даних...</p>
          </div>
        ) : (
          <>
            {/* Sticky Header */}
            <div className={`scheduler-sticky-header ${isMobile ? 'mobile' : ''}`}>
              <div className={isMobile ? `scheduler-collapsible ${isExpanded ? 'expanded' : 'collapsed'}` : undefined}>
                <FiltersSection
                  filters={filters}
                  pendingUpdates={pendingUpdates}
                  hasChanges={hasChanges}
                  isLoading={bulkUpdating}
                  onFilterChange={handleFilterChange}
                  onSaveAll={handleSaveAll}
                  onResetAll={handleResetAll}
                />
              </div>

              {isMobile && (
                <button className="scheduler-toggle-bar" onClick={() => setIsExpanded(e => !e)}>
                  <span className="scheduler-toggle-summary">
                    {!isExpanded && filters.status ? filters.status : 'Фільтри'}
                  </span>
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </button>
              )}

              <div className="scheduler-table-header-wrapper" ref={tableHeaderRef}>
                <TableHeader />
              </div>
            </div>

            {/* Results Table */}
            <ResultsTable
              entries={entries}
              expandedEntries={expandedEntries}
              isMobile={isMobile}
              bodyRef={tableBodyRef}
              onToggleExpanded={handleToggleExpanded}
              onStartEditing={startEditing}
              onCancelEditing={cancelEditing}
              onSaveEdit={saveEdit}
              onUpdateEditingValue={updateEditingValue}
              isEditing={isEditing}
              getEditingValue={getEditingValue}
              hasItemChanges={hasItemChanges}
            />
          </>
        )}

        {/* Confirmation Dialogs */}
        <ConfirmDialog
          open={confirmSaveOpen}
          title="Підтвердження збереження"
          message={`Ви дійсно хочете зберегти зміни у шедулері?`}
          confirmText="Зберегти"
          onConfirm={confirmSave}
          onCancel={() => setConfirmSaveOpen(false)}
          severity="warning"
        />

        <ConfirmDialog
          open={confirmTriggerOpen}
          title="Підтвердження обробки"
          message="Ви дійсно хочете запустити обробку всіх запланованих завдань?"
          confirmText="Запустити"
          onConfirm={confirmTriggerProcessing}
          onCancel={() => setConfirmTriggerOpen(false)}
          severity="info"
        />
      </div>
    </LocalizationProvider>
  );
}

export default SchedulerPage;
